import { useState } from "react";

interface Props {
  files: string[];
  activeFile: string;
  onSelect: (filename: string) => void;
  onCreate?: (filename: string) => void;
  onRename?: (oldName: string, newName: string) => void;
  onDelete?: (filename: string) => void;
}

const MANIFEST_KEY = "__manifest__";

function sanitizeName(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  // 拡張子付与・パス区切り除去
  const cleaned = t.replace(/^\/+/, "").replace(/\s+/g, "_");
  if (!cleaned.endsWith(".pix")) return `${cleaned}.pix`;
  return cleaned;
}

export function FileTree({
  files,
  activeFile,
  onSelect,
  onCreate,
  onRename,
  onDelete,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const sorted = files.slice().sort();

  function commitNew() {
    const name = sanitizeName(newName);
    if (!name) {
      setAdding(false);
      setNewName("");
      return;
    }
    if (files.includes(name)) {
      alert("同名のファイルが既に存在します");
      return;
    }
    onCreate?.(name);
    setAdding(false);
    setNewName("");
  }
  function commitRename(oldName: string) {
    const name = sanitizeName(renameValue);
    setRenaming(null);
    if (!name || name === oldName) return;
    if (files.includes(name)) {
      alert("同名のファイルが既に存在します");
      return;
    }
    onRename?.(oldName, name);
  }

  return (
    <div className="file-tree">
      <div className="file-tree-header">
        <span>FILES</span>
        {onCreate && (
          <button
            className="ft-action"
            title="新規ファイル"
            onClick={() => {
              setAdding(true);
              setNewName("");
            }}
          >
            ＋
          </button>
        )}
      </div>
      <ul>
        <li
          className={activeFile === MANIFEST_KEY ? "active" : ""}
          onClick={() => onSelect(MANIFEST_KEY)}
        >
          <span className="ft-icon">≡</span> project.pix.json
        </li>
        {sorted.map((f) =>
          renaming === f ? (
            <li key={f} className="ft-renaming">
              <input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => commitRename(f)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename(f);
                  else if (e.key === "Escape") setRenaming(null);
                }}
              />
            </li>
          ) : (
            <li
              key={f}
              className={activeFile === f ? "active" : ""}
              onClick={() => onSelect(f)}
              onDoubleClick={() => {
                if (!onRename) return;
                setRenaming(f);
                setRenameValue(f);
              }}
              title={onRename ? "ダブルクリックでリネーム" : ""}
            >
              <span className="ft-icon">≡</span>
              <span className="ft-name">{f}</span>
              {onDelete && (
                <button
                  className="ft-del"
                  title="削除"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`${f} を削除します。よろしいですか?`)) {
                      onDelete(f);
                    }
                  }}
                >
                  ✕
                </button>
              )}
            </li>
          ),
        )}
        {adding && (
          <li className="ft-renaming">
            <input
              autoFocus
              placeholder="新規ファイル名.pix"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={commitNew}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitNew();
                else if (e.key === "Escape") {
                  setAdding(false);
                  setNewName("");
                }
              }}
            />
          </li>
        )}
      </ul>
    </div>
  );
}
